import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShoppingBag, Euro, Download, Mail, Loader2 } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "";

const formatEur = (v) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v || 0);

const BandDuel = ({ perProduct }) => {
  const b1 = perProduct["schattenpsyche_band1"] || { sold: 0, revenue: 0, downloads: 0 };
  const b2 = perProduct["schattenpsyche_band2"] || { sold: 0, revenue: 0, downloads: 0 };
  const maxSold = Math.max(b1.sold, b2.sold, 1);
  const maxRev = Math.max(b1.revenue, b2.revenue, 0.01);

  const Row = ({ label, v1, v2, max, money }) => (
    <div className="space-y-2" data-testid={`duel-row-${label.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <span className="font-typewriter text-xs text-slate-400 w-16 shrink-0">Band I</span>
        <div className="flex-1 h-7 bg-white/5 rounded overflow-hidden">
          <div
            className="h-full bg-white transition-all duration-700"
            style={{ width: `${Math.max((v1 / max) * 100, v1 > 0 ? 4 : 0)}%` }}
          />
        </div>
        <span className="font-mono text-sm text-white w-20 text-right shrink-0">
          {money ? formatEur(v1) : v1}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-typewriter text-xs text-slate-400 w-16 shrink-0">Band II</span>
        <div className="flex-1 h-7 bg-white/5 rounded overflow-hidden">
          <div
            className="h-full bg-slate-500 transition-all duration-700"
            style={{ width: `${Math.max((v2 / max) * 100, v2 > 0 ? 4 : 0)}%` }}
          />
        </div>
        <span className="font-mono text-sm text-white w-20 text-right shrink-0">
          {money ? formatEur(v2) : v2}
        </span>
      </div>
      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-600 text-right -mt-1">{label}</p>
    </div>
  );

  return (
    <div className="glass rounded-xl p-6" data-testid="band-duel">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-typewriter text-lg text-paper">Das Duell</h2>
        <span className="stamp text-[10px]">Band I vs. Band II</span>
      </div>
      {b1.sold === 0 && b2.sold === 0 ? (
        <p className="text-slate-500 text-sm">Noch keine Verkäufe. Das Duell startet mit dem ersten Kauf.</p>
      ) : (
        <div className="space-y-6">
          <Row label="Verkäufe" v1={b1.sold} v2={b2.sold} max={maxSold} />
          <Row label="Umsatz" v1={b1.revenue} v2={b2.revenue} max={maxRev} money />
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 pt-2 border-t border-white/5">
            Downloads — Band I: {b1.downloads} · Band II: {b2.downloads}
          </p>
        </div>
      )}
    </div>
  );
};

export default function StatsPage() {
  const [key, setKey] = useState(sessionStorage.getItem("sp_stats_key") || "");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    if (!k) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stats`, { params: { key: k } });
      setStats(res.data);
      sessionStorage.setItem("sp_stats_key", k);
      setError("");
    } catch (e) {
      setError("Ungültiger Zugangscode.");
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = stats
    ? [
        { icon: ShoppingBag, label: "Verkäufe", value: stats.orders_paid, testId: "stats-card-orders" },
        { icon: Euro, label: "Umsatz", value: formatEur(stats.revenue_eur), testId: "stats-card-revenue" },
        { icon: Download, label: "Downloads", value: stats.downloads, testId: "stats-card-downloads" },
        { icon: Mail, label: "Leseproben", value: stats.leads, testId: "stats-card-leads" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-void text-white px-4 py-16 hero-grid">
      <Seo title="Statistik · SCHATTENPSYCHE" siteName="SCHATTENPSYCHE" />
      <div className="max-w-4xl mx-auto" data-testid="stats-page">
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo.png" alt="Schattenpsyche Logo" className="w-10 h-10 rounded-full ring-1 ring-white/20" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Interner Bereich</p>
            <h1 className="font-cinzel text-2xl sm:text-3xl font-bold">Verkaufs-Statistik</h1>
          </div>
        </div>

        {!stats && (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
            className="glass rounded-2xl p-8 max-w-md"
            data-testid="stats-login-form"
          >
            <label htmlFor="stats-key" className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
              Zugangscode
            </label>
            <input
              id="stats-key"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="••••••••••••"
              data-testid="stats-key-input"
              className="mt-3 w-full bg-white/5 border border-white/10 rounded-full px-5 py-3.5 text-white focus:outline-none focus:border-white/50 transition-colors"
            />
            {error && (
              <p className="mt-3 text-sm text-slate-400" data-testid="stats-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="stats-load-button"
              className="glow-btn w-full mt-5 !py-3.5 min-h-[52px] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Akte öffnen
            </button>
          </motion.form>
        )}

        {stats && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-cards">
              {cards.map((c) => (
                <div key={c.label} className="glass rounded-xl p-5" data-testid={c.testId}>
                  <c.icon className="w-5 h-5 text-slate-400 mb-3" />
                  <p className="font-cinzel text-2xl sm:text-3xl font-bold">{c.value}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">{c.label}</p>
                </div>
              ))}
            </div>

            <BandDuel perProduct={stats.per_product || {}} />

            <div className="glass rounded-xl p-6">
              <h2 className="font-typewriter text-lg text-paper mb-4">Letzte Verkäufe</h2>
              {stats.recent_orders.length === 0 ? (
                <p className="text-slate-500 text-sm">Noch keine bezahlten Bestellungen.</p>
              ) : (
                <div className="space-y-3" data-testid="stats-orders-list">
                  {stats.recent_orders.map((o, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-3 text-sm">
                      <div className="min-w-0">
                        <p className="text-white truncate">{o.title}</p>
                        <p className="text-slate-500 font-mono text-xs truncate">{o.email || "E-Mail folgt"}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-white">{formatEur(o.amount)}</p>
                        <p className="font-mono text-[10px] text-slate-500">
                          {formatDate(o.created_at)} · {o.downloads} Downloads
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="glass rounded-xl p-6">
              <h2 className="font-typewriter text-lg text-paper mb-4">Freigabe-Liste (Leseprobe)</h2>
              {stats.recent_leads.length === 0 ? (
                <p className="text-slate-500 text-sm">Noch keine Anmeldungen.</p>
              ) : (
                <div className="space-y-2" data-testid="stats-leads-list">
                  {stats.recent_leads.map((l, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-sm border-b border-white/5 pb-2">
                      <span className="text-slate-300 truncate">{l.email}</span>
                      <span className="font-mono text-[10px] text-slate-500">{formatDate(l.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => load(key)}
              data-testid="stats-refresh-button"
              className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-white transition-colors"
            >
              Aktualisieren
            </button>
          </motion.div>
        )}

        <Link
          to="/"
          data-testid="stats-home-link"
          className="inline-block mt-12 font-mono text-xs uppercase tracking-[0.25em] text-slate-600 hover:text-white transition-colors"
        >
          Zurück zur Seite
        </Link>
      </div>
    </div>
  );
}
